import { API_URLS } from "@/utils/constant/api.constant";
import { V2_API } from "@/utils/helper/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface OrganizationListParams {
  search?: string;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
  page?: number;
  per_page?: number;
}

interface Organization {
  id: number;
  name: string;
  slug: string;
  description?: string;
  org_type?: string;
  [key: string]: any;
}

interface OrganizationListResponse {
  data: Organization[];
  total: number;
  page: number;
  per_page: number;
  totalPages: number;
}

const fetchOrganizations = async (params: OrganizationListParams): Promise<OrganizationListResponse> => {
  const queryParams = new URLSearchParams();
  if (params.search) queryParams.append("search", params.search);
  if (params.sortBy) queryParams.append("sortBy", params.sortBy);
  if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.per_page) queryParams.append("per_page", params.per_page.toString());

  const response = await V2_API.get(`${API_URLS.INTERNAL.ORGANISATIONS.LIST}?${queryParams.toString()}`);
  const apiData = response.data?.data || response.data;
  
  // Map snake_case to camelCase for consistency
  return {
    data: apiData?.data || [],
    total: apiData?.total || 0,
    page: apiData?.page || 1,
    per_page: apiData?.per_page || 12,
    totalPages: apiData?.total_pages || 1, // Map total_pages to totalPages
  };
};

export const useOrganizations = (params: OrganizationListParams) => {
  return useQuery<OrganizationListResponse, Error>({
    queryKey: ["organizations", params],
    queryFn: () => fetchOrganizations(params),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

interface CreateOrganizationPayload {
  name: string;
  slug: string;
  description?: string;
  org_type?: string;
  [key: string]: any;
}

interface UpdateOrganizationPayload {
  id: number;
  name?: string;
  slug?: string;
  description?: string;
  org_type?: string;
  [key: string]: any;
}

interface DeleteOrganizationPayload {
  id: number;
}

const createOrganization = async (payload: CreateOrganizationPayload) => {
  const response = await V2_API.post(`${API_URLS.INTERNAL.ORGANISATIONS.MANAGE}?actionType=create`, payload);
  return { status: response.status, data: response.data };
};

const updateOrganization = async (payload: UpdateOrganizationPayload) => {
  const { id, ...updateData } = payload;
  const response = await V2_API.post(`${API_URLS.INTERNAL.ORGANISATIONS.MANAGE}?actionType=update&id=${id}`, {
    id,
    ...updateData,
  });
  return { status: response.status, data: response.data };
};

const deleteOrganization = async (payload: DeleteOrganizationPayload) => {
  const response = await V2_API.post(`${API_URLS.INTERNAL.ORGANISATIONS.MANAGE}?actionType=delete&id=${payload.id}`, payload);
  return { status: response.status, data: response.data };
};

export const useCreateOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
};

export const useDeleteOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
};

